/**
 * FornadaDeleteDialog Component
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fornadasAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import type { Fornada } from "@/types/fornada";
import { AlertTriangle } from "lucide-react";

interface FornadaDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fornada: Fornada | null;
  onConfirm: () => void;
}

export function FornadaDeleteDialog({ open, onOpenChange, fornada, onConfirm }: FornadaDeleteDialogProps) {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!fornada) return;

    setIsDeleting(true);
    try {
      await fornadasAdapter.delete(fornada.id_fornada);
      toast({
        title: "Sucesso",
        description: "Fornada excluída com sucesso",
      });
      onConfirm();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao excluir fornada",
        description: error.message,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir Fornada</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>Tem certeza que deseja excluir esta fornada?</p>
            {fornada?.estoque_debitado && (
              <div className="bg-destructive/10 p-3 rounded-md">
                <p className="text-sm font-medium text-destructive">
                  ⚠️ Atenção: Esta fornada já debitou ingredientes do estoque.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  A exclusão NÃO reverterá automaticamente as movimentações de estoque.
                </p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isDeleting ? "Excluindo..." : "Sim, excluir"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
